import React from "react";
import {
    useTheme,
    alpha,
    Grid,
    Stack,
    Box,
    Button,
    Typography,
    Divider,
    Tooltip,
    Chip,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import SimpleDialogWrapper from "components/SimpleDialogWrapper";
import MaskedInputWrapper from "components/MaskedInputWapper";
import FormControlWrapper from "components/FormControlWrapper";
import { ContentCopyTwoTone, Language } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { IconRocket } from "@tabler/icons";
import { TipsAndUpdates } from "@mui/icons-material";
import { useSubmitReferralMutation } from "Services/ReferralService";
import { showSuccessToaster, showErrorToaster } from "store/Shared.Slice";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const ReferralFormPopup = ({ openPopup, onClose, formValue, setFormValue }) => {
    const theme = useTheme();
    const { userToken } = useSelector((state) => state.sharedState);
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [submitReferral, { isLoading: isReferralSubmitting }] =
        useSubmitReferralMutation();

    const handleFormChange = (field, value) => {
        setFormValue({ ...formValue, [field]: value });
    };

    const referralCode = [];

    referralCode.push({
        label: "Website",
        value: `${window.location.origin}/register?referral=${userToken?.UserName}`,
        icon: <Language color="secondary" fontSize="small" />,
        endIcon: <ContentCopyTwoTone />,
    });

    const handleSubmitReferral = async () => {
        try {
            const model = {
                nameCharity: formValue.nameCharity.value || null,
                nameContact: formValue.nameContact.value || null,
                email: formValue.email.value || null,
                phoneNumber: formValue.phoneNumber.value || null,
            };

            const response = await submitReferral(model).unwrap();

            dispatch(showSuccessToaster(response.message));

            setFormValue({
                nameCharity: { value: "", error: "" },
                nameContact: { value: "", error: "" },
                email: { value: "", error: "" },
                phoneNumber: { value: "", error: "" },
            });
        } catch (error) {
            // Model Errors
            if (error.data?.errors != null) {
                const copyFormValue = { ...formValue };
                Object.keys(error.data?.errors).forEach((key) => {
                    if (copyFormValue[key]) {
                        copyFormValue[key].error = error.data?.errors[key][0];
                    }
                });

                setFormValue(copyFormValue);
                dispatch(showErrorToaster("Check for errors and try again"));
            }
            // Bad Request
            else if (error.data?.message != null) {
                dispatch(showErrorToaster(error.data?.message));
            }
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            dispatch(
                showSuccessToaster(
                    `Copied to clipboard. ${referralCode[0].value}`
                )
            );
        });
    };

    return (
        <SimpleDialogWrapper
            title={t("labels.referral_popup_title")}
            icon={<IconRocket fontSize="small" />}
            open={openPopup}
            onClose={onClose}
            maxWidth="sm"
            dialogContentProps={{
                sx: {
                    p: 2,
                    height: "auto",
                    minHeight: "200px",
                },
            }}
            ActionContents={
                <>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        onClick={onClose}
                    >
                        {t("buttons.close")}
                    </Button>
                </>
            }
        >
            <Stack gap={1}>
                <Box
                    p={1.5}
                    sx={[
                        { bgcolor: "grey.100", borderRadius: 2 },
                        theme.applyStyles("dark", {
                            bgcolor: "background.paper2",
                        }),
                    ]}
                    mb={1}
                    display="flex"
                    alignItems="center"
                >
                    <TipsAndUpdates color="warning" sx={{ mr: 1 }} />
                    <Typography variant="caption">
                        {t("labels.referralFormPopup")}
                    </Typography>
                </Box>
                <Box
                    sx={[
                        {
                            bgcolor: "grey.50",
                            p: 2,
                            borderRadius: 2,
                            mb: 1,
                        },
                        theme.applyStyles("dark", {
                            bgcolor: alpha(
                                theme.palette.background.paper2,
                                0.5
                            ),
                        }),
                    ]}
                >
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        {t("messages.charities_helping_charities")}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="text.mediumDark"
                        mt={1.5}
                    >
                        {t("messages.refer_charity_earn_bonus")}
                    </Typography>
                    <List sx={{ listStyleType: "disc", pl: 3 }} disablePadding>
                        <ListItem
                            sx={{ display: "list-item", py: 0.5 }}
                            disableGutters
                        >
                            <ListItemText
                                primaryTypographyProps={{
                                    fontSize: "0.75rem",
                                }}
                                primary={t("messages.refer_charity_line1")}
                            />
                        </ListItem>

                        <ListItem
                            sx={{ display: "list-item", py: 0.5 }}
                            disableGutters
                        >
                            <ListItemText
                                primaryTypographyProps={{
                                    fontSize: "0.75rem",
                                }}
                                primary={t("messages.refer_charity_line2")}
                            />
                        </ListItem>

                        <ListItem
                            sx={{ display: "list-item", py: 0.5 }}
                            disableGutters
                        >
                            <ListItemText
                                primaryTypographyProps={{
                                    fontSize: "0.75rem",
                                }}
                                primary={t("messages.refer_charity_line3")}
                            />
                        </ListItem>
                    </List>
                </Box>
                <Grid
                    container
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ "& .MuiFormControl-root": { my: 0 } }}
                >
                    <Grid item xs={12} sm={6}>
                        <FormControlWrapper
                            name="nameCharity"
                            label={t("labels.charity_name")}
                            type="text"
                            maxLength="40"
                            theme={theme}
                            required
                            value={formValue.nameCharity.value}
                            error={formValue.nameCharity.error}
                            onChange={(element) =>
                                handleFormChange("nameCharity", {
                                    value: element.target.value,
                                    error: "",
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                        <FormControlWrapper
                            name="nameContact"
                            label={t("labels.contact_name")}
                            type="text"
                            maxLength="40"
                            theme={theme}
                            value={formValue.nameContact.value}
                            error={formValue.nameContact.error}
                            required
                            onChange={(element) =>
                                handleFormChange("nameContact", {
                                    value: element.target.value,
                                    error: "",
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <MaskedInputWrapper
                            name="phoneNumber"
                            label={t("labels.phone_number")}
                            maskType="phone"
                            theme={theme}
                            required
                            value={formValue.phoneNumber.value}
                            error={formValue.phoneNumber.error}
                            onChange={(element) =>
                                handleFormChange("phoneNumber", {
                                    value: element.target.value,
                                    error: "",
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlWrapper
                            name="email"
                            label={t("labels.email")}
                            type="text"
                            maxLength="40"
                            required
                            theme={theme}
                            value={formValue.email.value}
                            error={formValue.email.error}
                            onChange={(element) =>
                                handleFormChange("email", {
                                    value: element.target.value,
                                    error: "",
                                })
                            }
                        />
                    </Grid>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        width="100%"
                        mt={1}
                    >
                        <LoadingButton
                            disableElevation
                            size="medium"
                            type="button"
                            variant="contained"
                            color="secondary"
                            onClick={handleSubmitReferral}
                            loading={isReferralSubmitting}
                        >
                            {t("buttons.submit")}
                        </LoadingButton>
                    </Box>

                    <Divider
                        sx={{ my: 2, width: "100%", color: "text.mediumDark" }}
                    >
                        {t("labels.or")}
                    </Divider>

                    <Box
                        p={1.5}
                        sx={[
                            {
                                bgcolor: "grey.100",
                                borderRadius: 2,
                                width: "100%",
                            },
                            theme.applyStyles("dark", {
                                bgcolor: "background.paper2",
                            }),
                        ]}
                        mb={1}
                        display="flex"
                        alignItems="center"
                    >
                        <TipsAndUpdates color="warning" sx={{ mr: 1 }} />
                        <Typography variant="caption">
                            {t("labels.referralShareFormPopup")}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        width="100%"
                        flexWrap="wrap"
                        gap={1.5}
                    >
                        {referralCode.map((contact, i) => (
                            <Tooltip
                                key={"referralCode" + i}
                                title={t("labels.referral_code_tooltip")}
                                arrow
                            >
                                <Chip
                                    icon={contact.icon}
                                    deleteIcon={contact.endIcon}
                                    onClick={() =>
                                        copyToClipboard(contact.value)
                                    }
                                    {...(contact.endIcon
                                        ? {
                                              onDelete: () =>
                                                  copyToClipboard(
                                                      contact.value
                                                  ),
                                          }
                                        : {})}
                                    color="secondary"
                                    size="small"
                                    label={t("labels.referral_code")}
                                    sx={{
                                        p: "12.8px 5px",
                                        fontSize: "0.75rem",
                                        letterSpacing: 0.5,
                                        cursor: "pointer",
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                </Grid>
            </Stack>
        </SimpleDialogWrapper>
    );
};

export default ReferralFormPopup;
